/**
 * CSS utility functions for handling class name transformations
 * Particularly useful for content coming from CMS systems like Contentful
 */

// Constants for better maintainability
const CSS_PREFIX = 'frw-'

const EXCLUDED_CLASS_PREFIXES = [
  'frw-',
  'fa-',
  'react-',
  'rt-',
  'slick-',
  'fg-',
  'mkto', // doesn't end in a dash, but many of the Marketo form classes start with this (mktoButton, mktoField, etc.),
  'ag-',
  'vjs-',
  'Toastify-',
  'Toastify_'
]

const FULLY_QUALIFIED_CLASSES = new Set([
  'fas',
  'fab',
  'far',
  'fal',
  'Toastify'
])
// Regular expressions
const CLASS_ATTRIBUTE_REGEX = /class\s*=\s*(['"])(.*?)\1/g
const STYLE_TAG_REGEX = /<style[^>]*>([\s\S]*?)<\/style>/gi

/**
 * Determines if a class name should be excluded from prefixing
 */
function shouldExcludeClassName(className: string): boolean {
  return (
    EXCLUDED_CLASS_PREFIXES.some((prefix) => className.startsWith(prefix)) ||
    FULLY_QUALIFIED_CLASSES.has(className)
  )
}

/**
 * Processes class names in HTML class attributes
 */
function processClassAttribute(
  match: string,
  quote: string,
  classNames: string
): string {
  const updatedClasses = classNames
    .split(/\s+/)
    .filter((cls) => cls.length > 0) // Remove empty classes
    .map((cls) => (shouldExcludeClassName(cls) ? cls : `${CSS_PREFIX}${cls}`))
    .join(' ')

  return `class=${quote}${updatedClasses}${quote}`
}

/**
 * Processes CSS content within a style tag
 */
function processStyleTagContent(match: string, cssContent: string): string {
  // Process CSS content while avoiding URLs and quoted strings
  const protectedStrings: string[] = []

  // Protect URLs: url("...") or url('...')
  let tempCSS = cssContent.replaceAll(
    /url\s*\(\s*(["'])(.*?)\1\s*\)/g,
    (match) => {
      const index = protectedStrings.length
      protectedStrings.push(match)
      return `__PROTECTED_URL_${index}__`
    }
  )

  // Protect other quoted strings in CSS (like font names, content values, etc.)
  tempCSS = tempCSS.replaceAll(/(["'])((?:\\.|(?!\1)[^\\])*)\1/g, (match) => {
    const index = protectedStrings.length
    protectedStrings.push(match)
    return `__PROTECTED_STRING_${index}__`
  })

  // Process CSS class selectors - handle both single and chained selectors
  // This regex finds .className patterns that are valid CSS selectors
  tempCSS = tempCSS.replaceAll(
    /\.([a-zA-Z][a-zA-Z0-9_-]*)/g,
    (match, className) => {
      return shouldExcludeClassName(className)
        ? match
        : `.${CSS_PREFIX}${className}`
    }
  )

  // Restore the protected strings
  for (let i = protectedStrings.length - 1; i >= 0; i -= 1) {
    tempCSS = tempCSS.replace(`__PROTECTED_URL_${i}__`, protectedStrings[i])
    tempCSS = tempCSS.replace(`__PROTECTED_STRING_${i}__`, protectedStrings[i])
  }

  return match.replace(cssContent, tempCSS)
}

/**
 * Prepends 'frw-' prefix to CSS classes in HTML strings and CSS selectors in style tags
 * Fixes issues where Contentful entries use old gameworks classes or need consistent prefixing
 *
 * @param html - HTML string containing class attributes and/or style tags
 * @returns HTML string with 'frw-' prefixed to custom classes and CSS selectors
 *
 * @example
 * ```typescript
 * const html = '<div class="container header">Content</div>'
 * const result = addPrefixToCSSClasses(html)
 * // Returns: '<div class="frw-container frw-header">Content</div>'
 *
 * const htmlWithStyle = '<style>.header { color: red; }</style><div class="header">Content</div>'
 * const result2 = addPrefixToCSSClasses(htmlWithStyle)
 * // Returns: '<style>.frw-header { color: red; }</style><div class="frw-header">Content</div>'
 * ```
 */
export function addPrefixToCSSClasses(html: string): string {
  // Handle undefined/null/empty inputs
  if (!html || typeof html !== 'string') {
    return ''
  }

  // Process class attributes in HTML elements
  const htmlWithPrefixedClasses = html.replaceAll(
    CLASS_ATTRIBUTE_REGEX,
    processClassAttribute
  )

  // Process CSS selectors within <style> tags
  return htmlWithPrefixedClasses.replaceAll(
    STYLE_TAG_REGEX,
    processStyleTagContent
  )
}

// Adds the "frw-" for a single className string
export function toFrwClassName(className: string | undefined): string {
  // refactor this function for falsy values (blank string, null, undefined, etc.)
  if (!className || typeof className !== 'string' || !className.trim()) {
    return ''
  }

  return className
    .split(/\s+/)
    .filter((cls) => cls.length > 0)
    .map((cls) => (shouldExcludeClassName(cls) ? cls : `${CSS_PREFIX}${cls}`))
    .join(' ')
}

/**
 * DOM-safe version of addPrefixToCSSClasses that preserves event handlers
 * Processes all elements with classes within a container without destroying DOM structure
 * Safe for elements with event handlers, form validation, etc.
 */

// Adding ESLint disable/enable to allow direct DOM manipulation
/* eslint-disable no-param-reassign */
export function addPrefixToCSSClassesDOM(container: HTMLElement): void {
  if (!container) {
    return
  }

  // Process the container itself if it has classes
  if (container.className) {
    const newClassName = toFrwClassName(container.className)
    if (newClassName !== container.className) {
      container.className = newClassName
    }
  }

  // Process all child elements with classes
  const elementsWithClasses = container.querySelectorAll('[class]')
  elementsWithClasses.forEach((element) => {
    if (element instanceof HTMLElement && element.className) {
      const newClassName = toFrwClassName(element.className)
      if (newClassName !== element.className) {
        element.className = newClassName
      }
    }
  })
}
/* eslint-enable no-param-reassign */
